import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'

import { reportUnauthorized } from '@/shared/api/unauthorized'
import { useApiClient } from '@/shared/api/use-api-client'
import { useSession } from '@/shared/auth/session'
import { LinkAccessProvider, useLinkAccess, type LinkAccess, type NavItem } from '@/shared/registry'

/** The API's answer for one session's caller, or the page it failed on, which a later page asks again after. */
type Answer = { readonly caller: string; readonly isOn: boolean; readonly failedOn?: never } | { readonly caller: string; readonly isOn?: never; readonly failedOn: string }

/**
 * Shows a nav item only while the API says the page it opens is on for the signed-in viewer, as
 * legacy's menu shows a learner page only while the viewer's organization has it on. It asks
 * `path` once per session, and only once the caller may otherwise see the item: a 2xx answer
 * shows it when `isOn` accepts the body, and any other 4xx hides it, for the rest of the session.
 * Until then, and after a failure or a 401, it stays hidden, and the next page asks again; a 401
 * also tells the session provider, which checks whether the session lapsed.
 */
export function NavItemSwitch(props: {
  readonly item: NavItem
  readonly path: string
  readonly isOn: (body: unknown) => boolean
  readonly children: ReactNode
}) {
  const { item, path, isOn } = props
  const client = useApiClient()
  const session = useSession().session ?? null
  // Who acts, on which site and for which organization: a change of any asks again.
  const caller = session === null ? null : [session.userProfileId, session.organizationProfileId, session.actingSiteId ?? '', session.impersonatorUserProfileId ?? ''].join('|')
  const outer = useLinkAccess()
  const mayAsk = caller !== null && outer(item)
  const { pathname } = useLocation()
  const [answer, setAnswer] = useState<Answer | null>(null)
  // A lapsed session forgets the answer, so signing in again, even as the same person, asks again.
  if (caller === null && answer !== null) {
    setAnswer(null)
  }
  const mine = answer !== null && answer.caller === caller ? answer : null
  const isAnswered = mine !== null && mine.isOn !== undefined
  const hasFailedHere = mine !== null && mine.failedOn === pathname

  useEffect(() => {
    if (!mayAsk || caller === null || isAnswered || hasFailedHere) {
      return undefined
    }
    const controller = new AbortController()
    const ask = async () => {
      let found: Answer
      try {
        const response = await client.getWithStatus(path, controller.signal)
        if (response.status === 401 && !controller.signal.aborted) {
          // The session may have lapsed: the session provider asks, and a later page asks here again.
          reportUnauthorized()
        }
        found = response.status >= 200 && response.status < 500 && response.status !== 401 ? { caller, isOn: response.status < 300 && isOn(response.body) } : { caller, failedOn: pathname }
      } catch {
        found = { caller, failedOn: pathname }
      }
      if (!controller.signal.aborted) {
        setAnswer(found)
      }
    }
    void ask()
    return () => controller.abort()
  }, [client, caller, mayAsk, isAnswered, hasFailedHere, path, isOn, pathname])

  const isShown = mine?.isOn === true
  const canSee = useCallback<LinkAccess>((entry, context) => (entry === item ? isShown && outer(entry, context) : outer(entry, context)), [item, isShown, outer])

  return <LinkAccessProvider value={canSee}>{props.children}</LinkAccessProvider>
}
